import { For, Show, createMemo, createSignal } from "solid-js";
import { MannequinConstruction, mannequinViews, type MannequinSex } from "./Mannequin";
import { MeasureRow } from "./MeasureRow";
import { BodyPicker, SaveProfileForm, type BodySource, type SavedProfileSummary } from "./BodyPicker";
import { linkClick } from "../core/router";
import { bodyMeasurementFor, type BodyMeasurementRow, type BodyType, type ChartStature } from "@tailor-frame/patterns";
import type { BodyMeasurements, Diagnostic, DraftResult, PatternProject, PatternSpecification, ValueDefinition } from "@tailor-frame/engine";

interface Props {
  specification: PatternSpecification;
  project: PatternProject;
  draft: DraftResult;
  bodyType: BodyType;
  bodySource: BodySource;
  madeFor: string;
  examples: Array<{ id: string; label: string; description: string; bodyType: BodyType }>;
  savedProfiles: SavedProfileSummary[];
  onBodyType: (type: BodyType) => void;
  onChartSize: (chartId: string, sizeId: string, stature: ChartStature["id"]) => void;
  onExample: (id: string) => void;
  onSavedProfile: (id: string) => void;
  onDeleteSavedProfile: (id: string) => void;
  onSaveProfile: (name: string) => void;
  onMeasurement: (id: string, value: number) => void;
  /** The shared body set (mm) every pattern reads from. */
  body: Record<string, number>;
  bodyRows: BodyMeasurementRow[];
  estimated: string[];
  onBodyValue: (id: string, value: number) => void;
  /** Body form of the client, set by their body type and size. */
  sex: MannequinSex;
  workspaceHref: string;
  onBodyMeasurement: (id: keyof BodyMeasurements, value: number) => void;
  onDownloadProject: () => void;
  source: string;
  onSource: (source: string) => void;
  onValidate: () => void;
  onApply: () => void;
  onDownloadSpecification: () => void;
  diagnostics: Diagnostic[];
  dirty: boolean;
}

const figureInputs: Array<{ id: keyof BodyMeasurements; label: string; minimum: number; maximum: number }> = [
  { id: "height", label: "Standing height", minimum: 1000, maximum: 2300 },
  { id: "headHeight", label: "Head height", minimum: 160, maximum: 320 },
  { id: "headWidth", label: "Head width", minimum: 110, maximum: 240 },
  { id: "headDepth", label: "Head depth", minimum: 130, maximum: 280 },
];

/** Measurements first, mannequin alongside, pattern source tucked away at the bottom. */
export function FitPage(props: Props) {
  const [active, setActive] = createSignal<string>();
  const groups = createMemo(() => {
    const grouped = new Map<string, Array<[string, ValueDefinition]>>();
    for (const entry of Object.entries(props.specification.measurements)) {
      const group = entry[1].group ?? "Measurements";
      grouped.set(group, [...(grouped.get(group) ?? []), entry]);
    }
    return [...grouped.entries()];
  });
  // Body measurements this pattern does not read itself, listed after the pattern's own.
  // Women's wear rows stay hidden on a male figure unless the pattern reads them.
  const linkedBodyIds = createMemo(() => new Set(Object.keys(props.specification.measurements).map(bodyMeasurementFor).filter(Boolean)));
  const otherGroups = createMemo(() => {
    const grouped = new Map<string, BodyMeasurementRow[]>();
    for (const row of props.bodyRows.filter((item) => !linkedBodyIds().has(item.id) && (props.sex === "female" || item.group !== "Women's"))) grouped.set(row.group, [...(grouped.get(row.group) ?? []), row]);
    return [...grouped.entries()];
  });
  const labels = createMemo(() => ({
    ...Object.fromEntries(props.bodyRows.map((row) => [row.id, row.label])),
    ...Object.fromEntries(Object.entries(props.specification.measurements).map(([id, definition]) => [id, definition.label])),
  }));
  // Figure guides: the pattern's measurements plus body ones it does not already cover.
  const figureValues = createMemo(() => ({
    ...Object.fromEntries(Object.entries(props.body).filter(([id]) => !linkedBodyIds().has(id as BodyMeasurementRow["id"]))),
    ...props.project.measurements,
  }));
  const errorCount = createMemo(() => props.diagnostics.filter((item) => item.severity === "error").length);

  return (
    <main class="fit-page">
      <section class="fit-controls" aria-label="Measurements">
        <header class="fit-heading">
          <p class="eyebrow">MEASUREMENTS</p>
          <h2>Who is this {props.specification.pattern.name.toLowerCase()} for?</h2>
          <BodyPicker bodyType={props.bodyType} source={props.bodySource} madeFor={props.madeFor} examples={props.examples} saved={props.savedProfiles} onBodyType={props.onBodyType} onChartSize={props.onChartSize} onExample={props.onExample} onSaved={props.onSavedProfile} onDeleteSaved={props.onDeleteSavedProfile} />
        </header>

        <p class="measure-section-title">Used by this pattern</p>
        <For each={groups()}>{([group, entries]) => (
          <fieldset class="measure-group">
            <legend>{group}</legend>
            <For each={entries}>{([id, definition]) => (
              <MeasureRow id={id} definition={definition} value={props.project.measurements[id]} active={active() === id} onActive={setActive} onChange={props.onMeasurement} />
            )}</For>
          </fieldset>
        )}</For>

        <Show when={otherGroups().length > 0}>
          <p class="measure-section-title">Other body measurements</p>
          <p class="fit-hint">Saved with {props.project.madeFor}'s body and shared by every pattern. Values marked <em>est.</em> are estimates until they are measured.</p>
          <For each={otherGroups()}>{([group, rows]) => (
            <fieldset class="measure-group">
              <legend>{group}</legend>
              <For each={rows}>{(row) => (
                <div class="body-measure" classList={{ estimated: props.estimated.includes(row.id) }} title={row.howToMeasure}>
                  <MeasureRow id={row.id} definition={{ type: "length", label: props.estimated.includes(row.id) ? `${row.label} (est.)` : row.label, minimum: row.minimum, maximum: row.maximum, default: row.minimum }} value={props.body[row.id]} active={active() === row.id} onActive={setActive} onChange={props.onBodyValue} />
                </div>
              )}</For>
            </fieldset>
          )}</For>
        </Show>

        <Show when={Object.values(props.specification.options).filter((definition) => !definition.hidden).length > 0}>
          <p class="fit-hint">Style options ({Object.values(props.specification.options).filter((definition) => !definition.hidden).length}) are in the <a href={props.workspaceHref} onClick={linkClick}>workspace</a>, beside the pieces they change.</p>
        </Show>

        <details class="fit-section">
          <summary>Figure (mannequin only)</summary>
          <For each={figureInputs}>{(input) => (
            <MeasureRow id={input.id} definition={{ type: "length", label: input.label, minimum: input.minimum, maximum: input.maximum, default: 0 }} value={props.project.bodyMeasurements[input.id]} active={false} onActive={() => undefined} onChange={(id, value) => props.onBodyMeasurement(id as keyof BodyMeasurements, value)} />
          )}</For>
        </details>

        <SaveProfileForm source={props.bodySource} madeFor={props.madeFor} onSave={props.onSaveProfile} />

        <div class="fit-actions"><button type="button" onClick={props.onDownloadProject}>Download measurements</button></div>
      </section>

      <section class="fit-stage" aria-label={`${props.sex} mannequin front, side, and back views`}>
        <div class="fit-stage-heading">
          <div><p class="eyebrow">MANNEQUIN</p><h2>{props.project.madeFor}</h2></div>
          <span>{active() ? labels()[active()!] : "All dimensions in millimetres"}</span>
        </div>
        <div class="mannequin-canvas mannequin-view-grid">
          <For each={mannequinViews}>{(view) => (
            <article class="mannequin-view-panel">
              <header><strong>{view.label}</strong></header>
              <MannequinConstruction model={props.draft.mannequin} sex={props.sex} view={view.id} values={figureValues()} body={props.body} active={active()} labels={labels()} />
            </article>
          )}</For>
        </div>
      </section>

      <details class="advanced-source" open={props.dirty || errorCount() > 0}>
        <summary>
          Advanced · pattern specification (YAML)
          <Show when={props.dirty}><span class="stale"> · unapplied edits</span></Show>
          <Show when={errorCount() > 0}><span class="error"> · {errorCount()} error(s)</span></Show>
        </summary>
        <div class="editor-toolbar">
          <button type="button" onClick={props.onValidate}>Validate</button>
          <button type="button" class="primary" onClick={props.onApply}>Apply draft</button>
          <button type="button" onClick={props.onDownloadSpecification}>Download YAML</button>
        </div>
        <textarea aria-label="Pattern YAML" spellcheck={false} value={props.source} onInput={(event) => props.onSource(event.currentTarget.value)} />
        <div class="diagnostics">
          <Show when={props.diagnostics.length === 0}><span>No validation errors.</span></Show>
          <For each={props.diagnostics}>{(item) => <span class={item.severity}>{item.code}: {item.message}</span>}</For>
        </div>
      </details>
    </main>
  );
}
